import { decodeBytes, ECDH_KEY_SPEC } from "open-signer-shared-cryptography";
import type { CrossmintApiService } from "../api";
import type { FrameLogger } from "../logger";
import { CrossmintFrameService } from "../service";
import type { AttestationService } from "./attestation";

/**
 * Development mode attestation service that bypasses TEE verification
 * and directly retrieves the public key from the API.
 */
export class AttestationDevService
	extends CrossmintFrameService
	implements AttestationService
{
	name = "Development Attestation Service";
	log_prefix = "[AttestationDevService]";

	constructor(
		private readonly api: CrossmintApiService,
		protected readonly logger: FrameLogger,
	) {
		super(logger);
	}

	private publicKey: CryptoKey | null = null;

	async init() {
		try {
			this.logger.debug(
				this.log_prefix,
				"Development mode: bypassing TEE attestation",
			);

			const { publicKey } = await this.api.getPublicKey();
			this.publicKey = await crypto.subtle.importKey(
				"raw",
				decodeBytes(publicKey, "base64"),
				ECDH_KEY_SPEC,
				true,
				[],
			);
			this.logger.debug(this.log_prefix, "Development public key retrieved");
		} catch (e: unknown) {
			this.logger.error(
				this.log_prefix,
				"Failed to retrieve development public key! This error is not recoverable",
			);
			this.publicKey = null;
			throw e;
		}
	}

	async getAttestedPublicKey(): Promise<CryptoKey> {
		if (!this.publicKey) {
			throw new Error("Attestation service has not been initialized!");
		}

		return this.publicKey;
	}
}
